import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Alert, Box } from '@mui/material';
import {
    MaterialBalanceGroupBy,
    MaterialBalancePageQuery,
    useMaterialBalanceExceptionsLazyQuery,
    useMaterialBalanceMachinesLazyQuery,
    useMaterialBalancePageLazyQuery,
} from '../../../../services';
import {
    CTableWrapper,
    CVerticalDropdown,
    DAutocomplete,
    DBlock,
    DBlockHeader,
    DIdAutocomplete,
    DMonth,
    DPage,
    DPageBody,
    DYear,
    ICustomTableWrapperProps,
    useDMonth,
    useDYear,
} from '../../../dum';
import { formatFloat } from '../../../../helpers';
import {
    MaterialBalanceChart,
    MaterialBalanceChartPoint,
} from './blocks/material-balance-chart';
import { MaterialBalanceMachines } from './blocks/material-balance-machines';
import { MaterialBalanceExceptions } from './blocks/material-balance-exceptions';
import { MaterialBalanceExportButton } from './material-balance-export-button';

type GroupByOption = {
    text: string;
    value: MaterialBalanceGroupBy;
};

// Week first, and it is the default: the week is the alarm grain. The same
// physics per shift swings ±25 % against a far tighter weekly spread, so day is
// drill-down for explaining a bad week — never the thing that raises the flag.
const groupByOptions: GroupByOption[] = [
    { text: 'Semana', value: MaterialBalanceGroupBy.Week },
    { text: 'Mes', value: MaterialBalanceGroupBy.Month },
    { text: 'Dia', value: MaterialBalanceGroupBy.Day },
];

// Module level so the memoised column definitions can use it without listing it
// as a dependency. It is pure — everything it needs comes from its argument.
type PeriodKey = {
    year: number;
    month?: number | null;
    week?: number | null;
    day?: number | null;
};

function periodLabel(record: PeriodKey): string {
    if (record.week !== null && record.week !== undefined) {
        return `Semana ${record.week} · ${record.year}`;
    }
    if (record.day !== null && record.day !== undefined) {
        return dayjs()
            .set('year', record.year)
            .set('month', (record.month || 1) - 1)
            .set('date', record.day)
            .format('DD MMM YYYY');
    }
    if (record.month !== null && record.month !== undefined) {
        return `${dayjs()
            .set('month', record.month - 1)
            .format('MMMM')} ${record.year}`;
    }
    return String(record.year);
}

type BalanceRecord =
    MaterialBalancePageQuery['getMaterialBalanceSummary'][number];

type SelectedPeriod = {
    label: string;
    // Zero-indexed for the backend argument, as everywhere else in the summaries.
    month: number | null;
    week: number | null;
};

export function MaterialBalance() {
    const [groupByOption, setGroupByOption] = useState<GroupByOption | null>(
        groupByOptions[0],
    );
    const [branchId, setBranchId] = useState<number | null>(null);
    const [selectedPeriod, setSelectedPeriod] = useState<SelectedPeriod | null>(
        null,
    );

    const { year, setYear } = useDYear({
        COMPONENT_KEY: MaterialBalance.name + '_Year',
        defaultValue: dayjs().year(),
    });

    const { month, setMonth } = useDMonth({
        COMPONENT_KEY: MaterialBalance.name + '_Month',
        defaultValue: null,
    });

    const [query, { data, loading }] = useMaterialBalancePageLazyQuery();
    const [
        exceptionsQuery,
        { data: exceptionsData, loading: exceptionsLoading },
    ] = useMaterialBalanceExceptionsLazyQuery();
    const [machinesQuery, { data: machinesData, loading: machinesLoading }] =
        useMaterialBalanceMachinesLazyQuery();

    useEffect(() => {
        if (year && groupByOption) {
            void query({
                variables: {
                    year: year,
                    month: month ?? null,
                    branchId: branchId,
                    groupBy: groupByOption.value,
                },
            });
            void exceptionsQuery({
                variables: {
                    year: year,
                    month: month ?? null,
                    branchId: branchId,
                },
            });
        }
    }, [year, month, branchId, groupByOption, query, exceptionsQuery]);

    // A period selected under one filter set means nothing under another.
    useEffect(() => {
        setSelectedPeriod(null);
    }, [year, month, branchId, groupByOption]);

    useEffect(() => {
        if (year && selectedPeriod) {
            void machinesQuery({
                variables: {
                    year: year,
                    month: selectedPeriod.month,
                    week: selectedPeriod.week,
                    branchId: branchId,
                },
            });
        }
    }, [year, branchId, selectedPeriod, machinesQuery]);

    const records = useMemo(() => {
        return data?.getMaterialBalanceSummary || [];
    }, [data]);

    // The chart reads oldest-to-newest; the table keeps the query's newest-first
    // order, which is what every other summary page does.
    const chartPoints = useMemo<MaterialBalanceChartPoint[]>(() => {
        return records
            .map((record) => ({
                label: periodLabel(record),
                // The generated field is `number | null | undefined`; the chart
                // treats "no consumption" as one case, so collapse it to null.
                difference_percentage: record.difference_percentage ?? null,
                kilos_consumed: record.kilos_consumed,
                kilos_produced: record.kilos_produced,
                kilos_waste: record.kilos_waste,
            }))
            .reverse();
    }, [records]);

    const totals = useMemo(() => {
        return records.reduce(
            (acc, record) => ({
                kilos_consumed: acc.kilos_consumed + record.kilos_consumed,
                kilos_produced: acc.kilos_produced + record.kilos_produced,
                kilos_waste: acc.kilos_waste + record.kilos_waste,
                runs_count: acc.runs_count + record.runs_count,
                runs_without_consumption:
                    acc.runs_without_consumption +
                    record.runs_without_consumption,
                runs_without_production:
                    acc.runs_without_production + record.runs_without_production,
            }),
            {
                kilos_consumed: 0,
                kilos_produced: 0,
                kilos_waste: 0,
                runs_count: 0,
                runs_without_consumption: 0,
                runs_without_production: 0,
            },
        );
    }, [records]);

    // Typed to the row type rather than `ICustomTableWrapperItem` (which is fixed
    // to `Record<any, any>`): `content` is a function property, so under
    // strictFunctionTypes its parameter is contravariant, and `Record<any, any>`
    // is not assignable to a specific record type. This also gives `item` the
    // real field types, which is what `periodLabel` needs.
    const tableItems = useMemo<
        ICustomTableWrapperProps<BalanceRecord>['items']
    >(() => {
        return [
            {
                title: 'Periodo',
                textAlign: 'left',
                cellGrow: 2,
                content: (item) => periodLabel(item),
            },
            {
                title: 'Bobina consumida',
                textAlign: 'right',
                content: (item) => formatFloat(item.kilos_consumed, 0),
            },
            {
                title: 'Bolsa producida',
                textAlign: 'right',
                content: (item) => formatFloat(item.kilos_produced, 0),
            },
            {
                title: 'Merma',
                textAlign: 'right',
                content: (item) => formatFloat(item.kilos_waste, 0),
            },
            {
                title: 'Diferencia kg',
                textAlign: 'right',
                content: (item) => formatFloat(item.kilos_difference, 0),
            },
            {
                title: 'Diferencia %',
                textAlign: 'right',
                content: (item) =>
                    item.difference_percentage === null ||
                    item.difference_percentage === undefined
                        ? 'sin consumo'
                        : `${formatFloat(item.difference_percentage)} %`,
            },
            {
                title: 'Corridas',
                textAlign: 'right',
                content: (item) => formatFloat(item.runs_count, 0),
            },
            {
                title: 'Sin consumo',
                textAlign: 'right',
                content: (item) =>
                    formatFloat(item.runs_without_consumption, 0),
            },
            {
                title: 'Sin producción',
                textAlign: 'right',
                content: (item) => formatFloat(item.runs_without_production, 0),
            },
        ];
    }, []);

    const totalsRow = useMemo(() => {
        const difference =
            totals.kilos_consumed -
            totals.kilos_produced -
            totals.kilos_waste;
        return [
            'Total',
            formatFloat(totals.kilos_consumed, 0),
            formatFloat(totals.kilos_produced, 0),
            formatFloat(totals.kilos_waste, 0),
            formatFloat(difference, 0),
            totals.kilos_consumed === 0
                ? 'sin consumo'
                : `${formatFloat(
                      (100 * difference) / totals.kilos_consumed,
                  )} %`,
            formatFloat(totals.runs_count, 0),
            formatFloat(totals.runs_without_consumption, 0),
            formatFloat(totals.runs_without_production, 0),
        ];
    }, [totals]);

    return (
        <DPage>
            <DPageBody disableGutters>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        // Single scroll container anchored to the page-body flex
                        // height so the stacked blocks scroll inside the page
                        // instead of overlapping each other. DBlock is
                        // flexGrow:1 + minHeight:0, which is right for a
                        // one-block page and wrong here: left unchecked the four
                        // blocks distribute and shrink to the viewport, and their
                        // content overflows on top of the next block. Pin each
                        // child to its natural height instead. Same pattern as
                        // sales-summary.
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        '& > *': { flexShrink: 0, flexGrow: 0 },
                    }}
                >
                    <DBlock>
                        <DBlockHeader subtitle={'Balance de materia'}>
                            <Box sx={{ display: 'flex' }}>
                                <Box sx={{ mr: 2 }}>
                                    <DYear setYear={setYear} year={year} />
                                </Box>
                                <Box sx={{ mr: 2 }}>
                                    <DMonth setMonth={setMonth} month={month} />
                                </Box>
                            </Box>
                            <Box sx={{ alignSelf: 'start' }}>
                                <CVerticalDropdown
                                    items={[
                                        {
                                            component: (
                                                <DAutocomplete
                                                    value={groupByOption}
                                                    label={'Agrupación'}
                                                    options={groupByOptions}
                                                    itemText={'text'}
                                                    itemValue={'value'}
                                                    onChange={(item) => {
                                                        setGroupByOption(item);
                                                    }}
                                                />
                                            ),
                                        },
                                        {
                                            component: (
                                                <DIdAutocomplete
                                                    id={branchId}
                                                    onChange={setBranchId}
                                                    itemText={'name'}
                                                    label={'Sucursal'}
                                                    options={
                                                        data?.getBranches || []
                                                    }
                                                />
                                            ),
                                        },
                                        {
                                            component: (
                                                <MaterialBalanceExportButton
                                                    disabled={!data || loading}
                                                    records={records}
                                                    exceptions={
                                                        exceptionsData?.getMaterialBalanceExceptions
                                                    }
                                                />
                                            ),
                                        },
                                    ]}
                                />
                            </Box>
                        </DBlockHeader>

                        {/* Permanent, not a tooltip. A reader who does not know
                            this will draw the wrong conclusion from a single bad
                            shift. */}
                        <Alert severity={'info'} sx={{ mb: 2 }}>
                            El consumo de bobina es aproximado: un rollo se
                            registra como consumido por completo aunque quede
                            material, y el sobrante se usa en la siguiente
                            corrida. Por eso una corrida o un turno pueden variar
                            ±25 % y la semana cerrar cerca de 0 %.{' '}
                            <strong>Compare por semana.</strong> Las dos cifras
                            tampoco se miden igual: la bobina consumida está
                            parcialmente pesada, mientras que la bolsa producida
                            se cuenta como bultos × peso del bulto.
                        </Alert>

                        <MaterialBalanceChart
                            points={chartPoints}
                            loading={loading}
                        />
                    </DBlock>

                    <DBlock>
                        <DBlockHeader subtitle={'Balance por periodo'} />
                        <Alert severity={'info'} sx={{ mb: 2 }}>
                            Diferencia = bobina consumida − bolsa producida −
                            merma. Positiva significa que se consumió más bobina
                            que el producto que salió; negativa, que salió
                            producto de bobina que nunca se restó. Ninguno de los
                            dos signos es el “bueno”. Doble clic en una fila para
                            ver el desglose por máquina (agrupación por semana o
                            mes).
                        </Alert>
                        <CTableWrapper
                            items={tableItems}
                            data={records}
                            totalsRow={
                                records.length > 0 ? totalsRow : undefined
                            }
                            loading={loading}
                            onRowDoubleClick={(item) => {
                                // Day grain has no machine equivalent: the
                                // machine query narrows by week or month only, so
                                // a day double-click would silently show the
                                // whole month.
                                if (
                                    item.day !== null &&
                                    item.day !== undefined
                                ) {
                                    return;
                                }
                                setSelectedPeriod({
                                    label: periodLabel(item),
                                    week: item.week ?? null,
                                    month:
                                        item.week !== null &&
                                        item.week !== undefined
                                            ? (month ?? null)
                                            : item.month !== null &&
                                              item.month !== undefined
                                            ? item.month - 1
                                            : null,
                                });
                            }}
                        />
                    </DBlock>

                    {selectedPeriod ? (
                        <MaterialBalanceMachines
                            records={
                                machinesData?.getMaterialBalanceByMachine || []
                            }
                            periodLabel={selectedPeriod.label}
                            loading={machinesLoading}
                        />
                    ) : null}

                    <MaterialBalanceExceptions
                        exceptions={
                            exceptionsData?.getMaterialBalanceExceptions
                        }
                        loading={exceptionsLoading}
                    />
                </Box>
            </DPageBody>
        </DPage>
    );
}
